/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { createRoot } from "react-dom/client";
import { App } from "./App";

function ensureHeadLink(rel: string, href: string) {
  const selector = `link[rel="${rel}"]`;
  const existing = document.head.querySelector<HTMLLinkElement>(selector);

  if (existing) {
    existing.href = href;
    return;
  }

  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
}

function ensurePwaHeadTags() {
  if (typeof document === "undefined") {
    return;
  }

  ensureHeadLink("manifest", "/manifest.webmanifest");
  ensureHeadLink("apple-touch-icon", "/icons/app-icon.svg");
}

function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (!window.isSecureContext && !isLocalhost) {
    return;
  }

  const register = async () => {
    try {
      await navigator.serviceWorker.register("/sw.js");
    } catch (error) {
      console.error("No se pudo registrar el service worker", error);
    }
  };

  if (document.readyState === "complete") {
    void register();
    return;
  }

  window.addEventListener("load", () => {
    void register();
  }, { once: true });
}

function start() {
  ensurePwaHeadTags();
  registerServiceWorker();
  const root = createRoot(document.getElementById("root")!);
  root.render(<App />);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
